import { Hidden } from '@material-ui/core';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ArcRoutes } from '../../../pages/arc/constants/routes';
import { usePersonManagementReset, useSearchListsReset } from '../../../pages/arc/hooks/usePersonManagementReset';
import { removeForbiddenFeature } from '../../ducks/authenticated-slice';
import { TcsmFeatures } from '../../models/features-model';
import { TcsmReportsLandingPage } from '../../models/tcsm-page-models';
import { BlockFactory } from '../block-factory';
import { GuidedProcessSupportBlock } from '../guided-process-support-block';
import { DividerSection } from '../layout/divider-section';
import { MainContent } from '../layout/main-content-container';
import { PageContainer } from '../layout/page-container';
import { PageHeader } from '../layout/page-header';
import { SideBarContent } from '../layout/sidebar-content';
import { WysiwygWithReactLink } from '../wysiwyg-with-react-link';
import { useBasePath } from '../../hooks/useBasePath';
import { buttonStyles } from '../../constants/theme/styles/components/buttonstyles';
import { ContentSpacer } from '../layout/generic-content-spacer';
import { useRoleObject } from '../../../pages/arc/components/useRoles';
import { AdminOfficer, DiocesanAdministrator, DiocesanAssociateAdministrator, JuniorGroupPlanAdmin, SeniorGroupPlanAdmin, SeniorOfficer } from '../../../pages/arc/constants/constant-strings';

interface Props {
    pageData: TcsmReportsLandingPage,
}

interface SubProps {
    title: string,
    description: string,
    actionLabel: string,
    actionPath: string,
}

export function TcsmReportsLandingPageView(props: Props) {
    const { pageData } = props;
    const dispatch = useDispatch();
    const history = useHistory();


    const roles = useRoleObject();
    const butttonClasses = buttonStyles();

    useEffect(() => {
        dispatch(removeForbiddenFeature(TcsmFeatures.InstitutionAdministratorSearch));
    }, []);

    usePersonManagementReset();
    useSearchListsReset();

    function redirectAction(newPath: string) {
        return history.push(newPath);
    }

    function isGroupAdmin() {
        const mlpsRoles = roles?.mlps?.map(a=> a.role_name);
        return mlpsRoles?.includes(SeniorGroupPlanAdmin) || mlpsRoles?.includes(JuniorGroupPlanAdmin);
    }

    function isInstitutionAdmin() {
        const erRoles = roles?.employee_roster?.map(a => a.role_name);

        return erRoles?.includes(SeniorOfficer) || erRoles?.includes(AdminOfficer);
    }

    function isDiocesanAdmin() {
        const dioRoles = roles?.institution_roster?.map(a => a.role_name);

        return dioRoles?.includes(DiocesanAdministrator) || dioRoles?.includes(DiocesanAssociateAdministrator);
    }

    function ReportsSectionContentView(subProps: SubProps) {
        return (
            <DividerSection removeDivider sectionText={subProps.title}>
                <ContentSpacer>
                    <WysiwygWithReactLink content={subProps.description} />
                </ContentSpacer>
                <button className={butttonClasses.secondaryButton} type='button' onClick={() => redirectAction(subProps.actionPath)} >{subProps.actionLabel}</button>
            </DividerSection>
        );
    }

    function BenefitsGroupContentView() {
        return isGroupAdmin()
            ? <ReportsSectionContentView
                title={pageData.benefitsGroupTitle}
                description={pageData.benefitsGroupDescription}
                actionLabel={pageData.benefitsGroupActionLabel}
                actionPath={useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.GroupsLanding}${ArcRoutes.ReportsBenefitsGroup}`)}
            />
            : null;
    }

    function BillingAccountContentView() {
        return isGroupAdmin()
            ? <ReportsSectionContentView
                title={pageData.billingAccountTitle}
                description={pageData.billingAccountDescription}
                actionLabel={pageData.billingAccountActionLabel}
                actionPath={useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.GroupsLanding}${ArcRoutes.BillingAccountsSearch}${ArcRoutes.ReportsBillingAccount}`)}
            />
            : null;
    }

    function InstitutionContentView() {
        return isInstitutionAdmin()
            ? <ReportsSectionContentView
                title={pageData.institutionTitle}
                description={pageData.institutionDescription}
                actionLabel={pageData.institutionActionLabel}
                actionPath={useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}${ArcRoutes.ReportsInstitution}`)}
            />
            : null;
    }

    function DiocesanContentView() {
        return isDiocesanAdmin()
            ? <ReportsSectionContentView
                title={pageData.diocesanTitle}
                description={pageData.diocesanDescription}
                actionLabel={pageData.diocesanActionLabel}
                actionPath={useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.ReportsDiocesan}`)}
            />
            : null;
    }

    function TcsmReportsLandingContentView() {
        return (
            <DividerSection topDivider>
                <BenefitsGroupContentView />
                <BillingAccountContentView />
                <InstitutionContentView />
                <DiocesanContentView />
            </DividerSection>);
    }

    return (

        <PageContainer>
            <MainContent>
                <PageHeader
                    headerText={pageData?.pageTitle}
                />
                <Hidden mdUp>
                    <SideBarContent>
                        <GuidedProcessSupportBlock title={pageData?.landingPageGpsTitle} contents={pageData?.landingPageGps} />
                    </SideBarContent>
                </Hidden>

                <TcsmReportsLandingContentView />

                {
                    pageData?.linkSectionContent?.map((a, idx) => <BlockFactory key={idx} blockData={a} />)
                }

                <DividerSection removeDivider sectionText={pageData?.guidesAndResourcesTitle}>
                    <div className='dashboard-resources-container'>
                        {
                            pageData?.guidesAndResourceContent?.map((a, idx) => <BlockFactory key={idx} blockData={a} />)
                        }
                    </div>
                </DividerSection>
                <WysiwygWithReactLink content={pageData?.guidesAndResourcesOptionalDescription} />
            </MainContent>
            <Hidden smDown>
                <SideBarContent>
                    <GuidedProcessSupportBlock title={pageData?.landingPageGpsTitle} contents={pageData?.landingPageGps} />
                </SideBarContent>
            </Hidden>
        </PageContainer>
    );
}

export default TcsmReportsLandingPageView;
